/**
 * 源站 orocasino.mx rem（HTML 内联脚本抄录）：
 * D=750, M=450, B=37.5
 * fontSize = min(max(clientWidth, 1), 450) / 750 * 37.5
 * 450 宽 → 22.5px；390 宽 → 19.5px。禁止套用 lor-us 的 375/16。
 */
const DESIGN_WIDTH = 750;
const MAX_WIDTH = 450;
const ROOT_FONT = 37.5;

export const ORO_MX_MAX_WIDTH = MAX_WIDTH;
const PC_BG_PATH = 'skins/oro-mx-h5/pc/pc-bg.png';
const PC_BG_CSS_VAR = '--oro-mx-pc-bg';

function oroMxPublicBase() {
  const raw = import.meta.env.BASE_URL || '/';
  return raw.endsWith('/') ? raw : `${raw}/`;
}

/** 源站 body `url(/assets/pc_bg-*.png)`；本地镜像并带上 Vite BASE_URL（/mx/）。 */
export function oroMxPcBgCssImage() {
  return `url("${oroMxPublicBase()}${PC_BG_PATH}")`;
}

export function computeOroMxRootFont(clientWidth: number) {
  const capped = Math.min(Math.max(clientWidth, 1), MAX_WIDTH);
  return (capped / DESIGN_WIDTH) * ROOT_FONT;
}

/** 源站 Ll()：设计稿 px → 实际 CSS px */
export function oroMxDesignToPx(design: number, rootFont: number) {
  return (design / ROOT_FONT) * rootFont;
}

/**
 * 源站 fh() 读 #app；本地 #app 是全宽挂载点，450 列是 .app-frame。
 * 无 frame 时按 cap 450 居中，禁止用视口右缘当锚。
 */
export function resolveOroMxFloatAnchor(
  frame: { bottom: number; left?: number; right: number; top: number } | null | undefined,
  viewport: { height: number; width: number },
) {
  if (frame) {
    const left = frame.left ?? frame.right - Math.min(Math.max(viewport.width, 1), MAX_WIDTH);
    return { left, top: frame.top, right: frame.right, bottom: frame.bottom };
  }
  const width = Math.min(Math.max(viewport.width, 1), MAX_WIDTH);
  const left = (viewport.width - width) / 2;
  return { left, top: 0, right: left + width, bottom: viewport.height };
}

/** 源站 k()：拖拽夹在 #app 内，底边再扣 kBe(120) 底栏。 */
export function clampOroMxFloatPosition(
  x: number,
  y: number,
  app: { bottom: number; left: number; right: number; top: number },
  rootFont: number,
) {
  const btn = oroMxDesignToPx(120, rootFont);
  const bottomInset = oroMxDesignToPx(120, rootFont);
  const min = (value: number, lo: number, hi: number) => Math.min(Math.max(value, lo), hi);
  return {
    x: min(x, app.left, app.right - btn),
    y: min(y, app.top, app.bottom - bottomInset - btn),
  };
}

/**
 * 源站 EBe()：jackpot 叠在 scratch 上方，scratch / sign 用各自槽位，wheel 用 activity 槽。
 * 设计常量：btn 120、gap 12、bottomInset 120、lift 112。
 */
export function computeOroMxFloatLayout(
  app: { bottom: number; right: number; top: number },
  rootFont: number,
) {
  const btn = oroMxDesignToPx(120, rootFont);
  const gap = oroMxDesignToPx(12, rootFont);
  const inset = oroMxDesignToPx(120, rootFont);
  const lift = oroMxDesignToPx(112, rootFont);
  const wheelSlotY = app.bottom - inset - lift - btn;
  const activityY = wheelSlotY - btn - gap;
  const signY = activityY - btn - gap;
  const scratchY = signY - btn - gap;
  const jackpotY = scratchY - btn - gap;
  const x = app.right - btn;
  return {
    jackpot: { x, y: Math.max(app.top, jackpotY), z: 1204 },
    scratch: { x, y: Math.max(app.top, scratchY), z: 1203 },
    sign: { x, y: Math.max(app.top, signY), z: 1202 },
    wheel: { x, y: Math.max(app.top, activityY), z: 1200 },
  };
}

export function applyOroMxRem() {
  const width = document.documentElement.clientWidth || window.innerWidth;
  const size = computeOroMxRootFont(width);
  document.documentElement.style.fontSize = `${size}px`;
  document.documentElement.style.setProperty(PC_BG_CSS_VAR, oroMxPcBgCssImage());
  document.documentElement.dataset.oroMxRem = '1';
}

export function clearOroMxRem() {
  if (document.documentElement.dataset.oroMxRem !== '1') return;
  document.documentElement.style.fontSize = '';
  document.documentElement.style.removeProperty(PC_BG_CSS_VAR);
  delete document.documentElement.dataset.oroMxRem;
}

export function bindOroMxRem() {
  applyOroMxRem();
  const onResize = () => applyOroMxRem();
  window.addEventListener('resize', onResize);
  return () => {
    window.removeEventListener('resize', onResize);
    clearOroMxRem();
  };
}
